<template>
  <div>
    <v-layout wrap>
      <v-flex xs12 offset-md0 md12 lg12 offset-lg0>
        <v-layout wrap class="requested-home-tours__mls-wrapper">
          <v-flex xs5 md4 lg4 offset-m0 offset-lg0>
            <input
              id="mlsAddress"
              v-model="mlsInputValue"
              type="text"
              name="mlsAddress"
              placeholder="MLS# only"
              :class="{
                'mls-error-class': errorInput,
                'disable-input': !enableSelectors
              }"
            >
            <div
              v-if="errorInput || successInput"
              class="mls-error-text"
              :class="errorInput ? 'red--text' : 'green--text'"
            >
              {{ mlsErrorMessage }}
            </div>
          </v-flex>
          <v-flex xs2 md2 lg2 :class="{ 'disable-input': !enableSelectors }">
            <loader-button
              :loading="mlsSearch"
              :disabled="mlsSearch"
              :btn-settings="{
                color: colors.hwayzGray,
                small: true,
                round: true
              }"
              :btn-text="'Add Home'"
              :btn-class="'requested-home-button'"
              @handle-button-click="newHomeAdded"
            />
          </v-flex>
          <v-flex
            xs2
            md3
            lg2
            offset-md2
            offset-xs2
            offset-lg3
            class="requested-home-tours__house-btn"
          >
            <v-btn
              v-if="selectedHouses && selectedHouses.length > 0"
              :disabled="!enableSelectors"
              small
              round
              dark
              :color="$btnHwayzBlue"
              @click="redirectToRoutePlanning"
            >
              PLAN ROUTE
            </v-btn>
            <v-dialog
              v-else
              v-model="openPlanRouteModal"
              :width="alert.width"
              :max-width="alert.small_maxWidth"
            >
              <v-btn
                slot="activator"
                :disabled="!enableSelectors"
                small
                round
                dark
                :color="$btnHwayzBlue"
              >
                PLAN ROUTE
              </v-btn>
              <no-home-selected :on-close-modal="closeWarningModal" />
            </v-dialog>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <updated-list-component
      v-if="selectedClientHomes"
      :table-headers="requestedHomeTableHeader"
      :table-data="requestedHomeTableData"
      :custom-header="true"
      class="homes-container"
      @clicked="openConfirmationModal"
      @checkboxClicked="updateSelectedHomes"
      @property-loaded="enableSelectors = $event"
      @action-clicked="checkboxToggle($event)"
      @sort-changed="handleSorting($event)"
    />
    <v-dialog
      v-model="confirmationModal.status"
      :width="alert.width"
      :max-width="alert.maxWidth"
    >
      <confirmation-modal
        :modal-options="confirmationModal"
        @on-negative-clicked="onCancelDelete"
        @on-positive-clicked="removePropertyFromSavedHomes"
      />
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import colors from '../../../scss/_colors.scss'
import { mapState, mapMutations } from 'vuex'
import NoHomeSelected from '../../modals/NoHomeSelected.vue'
import ConfirmationModal from '../../modals/ConfirmationModal.vue'
import UpdatedListComponent from '../../uiUtils/UpdatedListComponent.vue'
import LoaderButton from '../../uiUtils/LoaderButton.vue'
import { UpdateDeleteFavoriteUrl } from '../../../utils/clientUrls'
import { isEmpty } from '../../../utils/generalUtils'
import {
  genericPutRequestToApi,
  genericPostRequestToApi
} from '../../../utils/genericRequests'
import {
  SEARCH_LIMIT,
  HIDE_STATUSES,
  INITIAL_NEW_QUERY
} from '../../common/searchPropertyConst'
import { RetrieveSearchPropertyUrl } from '../../../utils/agentUrls'
import refreshContent from '../../mixins/refreshContent'
import refreshClientData from '../../mixins/refreshClientData'
import { DEFAULT_CONFIRMATION_MODAL_VALUE } from '../../../utils/generalConsts'

export default {
  components: {
    NoHomeSelected,
    ConfirmationModal,
    UpdatedListComponent,
    LoaderButton
  },

  filters: {
    dateFormat: value => {
      return moment(value).format('MM-DD-YYYY')
    }
  },

  mixins: [refreshContent, refreshClientData],

  props: {
    selectedClientHomes: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      confirmationModal: Object.assign({}, DEFAULT_CONFIRMATION_MODAL_VALUE),
      requestedHomeTableHeader: [],
      requestedHomeTableData: [],
      selectedForRemoval: null,
      selectedHouses: [],
      openPlanRouteModal: false,
      enableSelectors: false,
      errorInput: false,
      successInput: false,
      mlsInputValue: '',
      mlsErrorMessage: '',
      mlsSearch: false,
      tableItems: [],
      colors,
      sortOptions: {
        descending: false
      }
    }
  },

  computed: {
    ...mapState('style', ['alert']),
    ...mapState('agent', ['rhtValue'])
  },

  watch: {
    enableSelectors(newValue, oldValue) {
      if (newValue && !oldValue) {
        this.generateHeaders()
        this.generateTableItems(this.selectedClientHomes)
        this.$emit('disable-tabs', false)
      }
    },

    selectedHouses: {
      handler: function(newValue, oldValue) {
        this.generateTableItems(this.selectedClientHomes)
      },
      deep: true
    },

    selectedClientHomes: {
      handler: function(newValue, oldValue) {
        this.enableSelectors = false
        this.generateTableItems(newValue)
      },
      deep: true
    }
  },

  created() {
    this.$emit('disable-tabs', true)
    this.generateHeaders()
    this.generateTableItems(this.selectedClientHomes)
  },

  methods: {
    ...mapMutations('home', {
      setActiveRoute: 'SET_ACTIVE_ROUTE'
    }),
    ...mapMutations('common', {
      setRouteHomes: 'SET_ROUTE_HOMES'
    }),
    ...mapMutations('client', {
      setClientData: 'SET_CLIENT_DATA'
    }),
    ...mapMutations('agent', {
      setSelectedStartLocation: 'SET_SELECTED_START_LOCATION',
      setSelectedEndLocation: 'SET_SELECTED_END_LOCATION',
      setRouteEditMode: 'SET_ROUTE_EDIT_MODE',
      setRht: 'SET_RHT'
    }),

    // Find property by entered MLS# and check if exists
    async newHomeAdded() {
      this.mlsSearch = true
      if (isEmpty(this.mlsInputValue)) {
        this.setMLSResult('Please enter an MLS value')
      } else {
        let query = JSON.parse(JSON.stringify(INITIAL_NEW_QUERY))
        query = { ...query, MLSNumber: this.mlsInputValue, Status: '' }

        const searchElement = {
          index: {
            skip: 0,
            limit: SEARCH_LIMIT
          },
          query
        }

        const response = await genericPostRequestToApi(
          RetrieveSearchPropertyUrl(),
          searchElement
        )

        if (isEmpty(response.data)) {
          this.setMLSResult('No property with that MLS# exists')
        } else if (response.data.length > 0) {
          let requestedProperty = null
          const propertyId = response.data[0]._id
          let isFound = this.clientData.homes.find(
            fav => fav.propertyId === propertyId
          )
          if (!isEmpty(isFound)) {
            if (isFound.requestTour) {
              this.setMLSResult(
                `Property with that MLS Number already exists below`
              )
            } else {
              requestedProperty = await this.requestTourForProperty(isFound)
              isFound = {
                ...isFound,
                ...this.clientFavorites.find(home => home._id === isFound.propertyId)
              }
              this.setMLSResult(
                `Property with MLS Number ${this.mlsInputValue} is successfully added to the list`,
                'success'
              )
            }
          } else {
            const propertyObject = await this.setFavorite(propertyId)
            requestedProperty = await this.requestTourForProperty(
              propertyObject
            )
            this.setMLSResult(
              `Property with MLS Number ${this.mlsInputValue} is successfully added to the list`,
              'success'
            )
            isFound = response.data[0]
          }
          if (requestedProperty && requestedProperty.propertyId) {
            requestedProperty = {
              ...isFound,
              ...requestedProperty,
              favoriteId: requestedProperty._id,
              _id: isFound._id
            }
            this.setRht({
              ...this.rhtValue,
              data: [requestedProperty, ...this.rhtValue.data]
            })
          }
        }
      }
    },

    setMLSResult(message, type = 'error') {
      this.errorInput = type === 'error'
      this.successInput = type === 'success'
      this.mlsErrorMessage = message
      this.mlsSearch = false
      this.mlsInputValue = ''
      setTimeout(() => {
        this.errorInput = false
        this.successInput = false
      }, 3000)
    },

    // Select - Deselect all checkbox related methods
    checkboxToggle(value) {
      value ? this.selectActiveHouses() : this.clearAllHouses()
    },

    handleSorting(value = false) {
      if (value) {
        this.sortOptions = value
      }

      this.requestedHomeTableData = _.orderBy(
        this.requestedHomeTableData,
        iterator => {
          return this.getOrderValue(iterator[1].text)
        },
        [this.sortOptions.descending ? 'desc' : 'asc']
      )
    },

    getOrderValue(status) {
      const order = {
        Active: 0,
        'Active Contingent': 1,
        Pending: 2,
        'Pending - Taking Backups': 3,
        Leased: 4,
        Expired: 5,
        Withdrawn: 6,
        Sold: 7,
        'Temporarily Off Market': 8
      }
      return order[status]
    },

    updateSelectedHomes(payload) {
      const _id = payload.item.item[0].data._id

      // If true (selected), filters through selectedClientHomes for _id of payload.
      // If false (deselected), filters through selectedHouses to take payload off.
      switch (payload.selected) {
        case true:
          const selectedHome = this.selectedClientHomes.find(
            home => home._id === _id
          )
          this.selectedHouses.push(selectedHome)
          break
        case false:
          this.selectedHouses = this.selectedHouses.filter(
            home => home._id !== _id
          )
          break
        default:
          console.log('must receive true or false')
          break
      }
    },

    generateHeaders() {
      this.requestedHomeTableHeader = [
        {
          text: 'Home Address',
          value: 'address',
          width: '50%',
          sortable: false,
          class: 'text-xs-left'
        },
        {
          text: 'Status',
          value: 'status',
          class: 'text-xs-left',
          sortable: true
        },
        { text: 'Remove', value: 'remove', sortable: false, align: 'center' },
        {
          type: 'checkbox',
          selected: false,
          text: 'Plan Route',
          value: 'planroute',
          sortable: false,
          align: 'center',
          class: this.enableSelectors ? null : 'disable'
        }
      ]
    },

    generateTableItems(fromArray) {
      let response = []
      fromArray.forEach((item, index) => {
        let obj = {}
        obj[0] = { type: 'addressImg', data: item }
        obj[1] = {
          text: item.Status,
          class: `text-xs-left ${this.getStatusClass(item.Status)}`
        }
        obj[2] = {
          type: 'clickableIcon',
          text: 'close',
          btnClass: `gray black--text test ${
            !this.enableSelectors ? 'disable' : null
          }`,
          data: item
        }

        const isShownCheckBox = this.isShownCheckBox(item.Status)

        if (isShownCheckBox) {
          obj[3] = {
            type: 'checkbox',
            selected: this.selectedHouses.some(house => house._id === item._id),
            class: this.enableSelectors ? null : 'disable'
          }
        } else {
          obj[3] = {
            type: 'text'
          }
        }

        response.push(obj)
      })
      if (response.length < 1) this.enableSelectors = true
      this.requestedHomeTableData = response
      this.handleSorting()
    },

    clearAllHouses() {
      if (this.selectedHouses.length > 0) {
        this.selectedHouses = []
      }
    },

    selectActiveHouses() {
      this.selectedHouses = [
        ...this.selectedClientHomes.filter(item =>
          this.isShownCheckBox(item.Status)
        )
      ]
    },

    isShownCheckBox(status) {
      return !HIDE_STATUSES.includes(status)
    },

    getStatusClass(status) {
      if (status === 'Active' || status === 'Active Contingent') {
        return 'green-status'
      }
      if (status === 'Pending' || status === 'Pending - Taking Backups') {
        return 'yellow-status'
      }
      return ''
    },

    /**
     * Handling opening of confirmation modal
     * If agent accepts it, then the property is removed from the list
     *
     * @param {Object} houseData house object selected for removal
     */
    openConfirmationModal(houseData) {
      this.selectedForRemoval = houseData
      Object.assign(this.confirmationModal, {
        status: true,
        message: `Do you really want to remove this property from adding to the route`,
        positive: 'Yes, remove it',
        negative: 'No, keep it'
      })
    },

    /**
     * Handles negative confirmation for removal of property
     */
    onCancelDelete() {
      this.confirmationModal = Object.assign(
        {},
        DEFAULT_CONFIRMATION_MODAL_VALUE
      )
    },

    async removePropertyFromSavedHomes() {
      const response = await genericPutRequestToApi(
        UpdateDeleteFavoriteUrl(
          this.clientData._id,
          this.selectedForRemoval.favoriteId
        ),
        null,
        { params: { requestTour: false } }
      )
      let updatedClientFav = this.clientFavorites.find(
        fav => fav.propertyId === this.selectedForRemoval.propertyId
      )

      if (response.status === 200) {
        const updatedHomes = this.clientData.homes.map(home => {
          if (home.propertyId === this.selectedForRemoval.propertyId) {
            updatedClientFav = {
              ...updatedClientFav,
              ...response.data,
              favoriteId: response.data._id,
              _id: updatedClientFav._id
            }
            return response.data
          }
          return home
        })
        this.setClientData(
          Object.assign({}, this.clientData, { homes: updatedHomes })
        )
        const updatedRhtHomes = this.rhtValue.data.filter(
          home => home.propertyId !== this.selectedForRemoval.propertyId
        )
        const updatedFavHomes = this.clientFavorites.map(fav => {
          if (fav.propertyId === updatedClientFav.propertyId) {
            return updatedClientFav
          }
          return fav
        })
        this.updatePropertyStates({ updatedRhtHomes, updatedFavHomes })
        this.onCancelDelete()
      }
    },

    closeWarningModal() {
      this.openPlanRouteModal = false
    },

    /**
     * Handles redirecting to creation of a new route
     * when some homes are selected to create a new route
     */
    redirectToRoutePlanning() {
      this.setRouteHomes(this.selectedHouses)
      this.setRouteEditMode(true)
      this.setSelectedStartLocation({})
      this.setSelectedEndLocation({})
      this.$router.push({
        name: 'plan-route'
      })
      this.setActiveRoute('plan-route')
    }
  }
}
</script>

<style lang="sass">
.green-status
  color: $hwayzGreen
.yellow-status
  color: $hwayzYellow
</style>

<style lang="sass" scoped>
.requested-home-tours__mls-wrapper
    padding-top: 2rem
    margin-bottom: 2rem
    margin-left: 2rem
#mlsAddress
  outline: none
  width: 100%
  height: 4rem
  border-bottom: 0.1rem solid $hwayzShadow

.mls-error-class
  border-bottom: 0.1rem solid $hwayzRed !important
.mls-error-text
  font-size: 1rem
  text-align: left

.select-all-button
  font-size: $hwayzFont1rem2 !important
  height: 2.8rem

.select-btn-container
  margin: 3rem 4rem
  float: left

.requested-home-tours__fab
  height: 2.4rem !important
  width: 2.4rem !important

.requested-home-tours__house-btn
  // margin: 1.5rem 0
  text-align: right

.requested-home-tours__house-btn #btnContent
  font-weight: 300
  font-size: $hwayzFont1rem2

.requested-home-tours__route-header
  padding: 2rem
  font-size: $hwayzFont1rem4

.requested-home-tours__home-image
  height: auto
  max-width: 12rem
  padding: 1rem 0

#addHomesToRoute
  .v-expansion-panel__header
    padding: 1rem 2rem !important
  .v-expansion-panel__header__icon
    display: none !important
  .v-btn
    font-size: $hwayzFont0rem7 !important
    float: right
  .modal__header
    padding-top: 1.2rem

@media only screen and (min-width: 960px)
  .requested-home-tours__mls-wrapper
    padding-top: 0

#tabContainer td
  text-align: left !important
  padding-right: 0 !important

.v-tabs__div
  color: $hwayzNavigationTab !important
  min-width: 20rem

.tab-container__tab
  background-color: $hwayzNavigationTab
  color: $hwayzWhite !important
  border-top-left-radius: 0.9rem
  border-top-right-radius: 0.9rem

.tab-container__tab:hover
  color: $hwayzWhite !important

.tab-container__content
  padding: 2rem
  min-height: 10rem

.disable-input
  @extend %disabled-state

.table
  @extend %plannerTable

.boldRow
  font-weight: map-get($text, boldText)
.homes-container
  overflow: scroll
  max-height: calc(100vh - 276px)
</style>
